import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllUsers } from '../utils/api/userAPI';

const ITEMS_PER_PAGE = 20; 

const ViewAllUserLayer = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((Number(totalRecords) || 0) / ITEMS_PER_PAGE));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const [isLoading, setIsLoading] = useState(false);

  // initialize page from URL
  useEffect(() => {
    const pFromURL = Number(searchParams.get('page')) || 1;
    setPage(pFromURL < 1 ? 1 : pFromURL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  // keep URL in sync when page changes
  useEffect(() => {
    const np = new URLSearchParams(searchParams);
    np.set('page', String(page));
    setSearchParams(np, { replace: true });
  }, [page, searchParams, setSearchParams]);

  // fetch users whenever page changes
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      setIsLoading(true);
      try {
        const data = await getAllUsers(page, ITEMS_PER_PAGE);
        if (isCancelled) return;
        setUsers(Array.isArray(data?.users) ? data.users : []);
        setTotalRecords(Number(data?.total || 0));
      } catch (e) {
        if (!isCancelled) {
          console.error('Failed to load users:', e?.message || e);
          setUsers([]);
          setTotalRecords(0);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };
    run();
    return () => { isCancelled = true; };
  }, [page]);

  // derived display range
  const startIdx = totalRecords === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;
  const endIdx = Math.min(page * ITEMS_PER_PAGE, totalRecords);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="col"
      style={{ height: 'auto' }}
    >
      <div className="card p-0 overflow-hidden position-relative radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                name="search"
                placeholder="Search"
                // (optional) wire this up to backend filtering later
                disabled
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>

          <Link
            to="/add-user"
            className="btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            style={{ backgroundColor: 'var(--primary-600)', color: 'var(--white)' }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add New User
          </Link>
        </div>

        <div className="card-body p-24 pt-10">
          {/* Table */}
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col text-center" className='text-center'>
                    <div className="">ID</div>
                  </th>
                  <th scope="col" className='text-center'>Full Name</th>
                  <th scope="col" className='text-center'>Email</th>
                  <th scope="col" className='text-center'>Phone Number</th>
                  <th scope="col" className='text-center'>Organisation</th>
                  <th scope="col" className="text-center">Status</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`shimmer-row-${idx}`}>
                      <td><div className="shimmer shimmer-id" /></td>
                      <td><div className="shimmer shimmer-text" /></td>
                      <td><div className="shimmer shimmer-text" /></td>
                      <td><div className="shimmer shimmer-text" /></td>
                      <td><div className="shimmer shimmer-text" /></td>
                      <td className="text-center"><div className="shimmer shimmer-badge" /></td>
                    </tr>
                  ))
                ) : users.length > 0 ? (
                  users.map((item) => (
                    <tr key={item?.id}>
                      <td className='text-center'>
                        <div className="d-flex align-items-center gap-10">
                          {item?.id}
                        </div>
                      </td>
                      <td className='text-center'>{item?.displayName}</td>
                      <td className='text-center'>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item?.email}
                        </span>
                      </td>
                      <td className='text-center'>{item?.phone}</td>
                      <td className='text-center'>{item?.organizationName}</td>
                      <td className="text-center">
                        {item?.status ? (
                          <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                            Active
                          </span>
                        ) : (
                          <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                            InActive
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '52vh' }}>
                        <p className="text-secondary-light text-md mb-16">No users found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination (Prev / Next like ChatLogsLayer) */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {startIdx} to {endIdx} of {totalRecords} entries
            </span>

            <div className="d-flex gap-2 ms-auto" style={{ justifyContent: 'flex-end' }}>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev || isLoading}
              >
                Prev
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage((p) => (canNext ? p + 1 : p))}
                disabled={!canNext || isLoading}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ViewAllUserLayer;
